import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DollarSign, Loader2, AlertCircle, FileBarChart, Settings } from 'lucide-react';
import { PageLayout } from '@/components/ui/PageLayout';
import Button from '@/components/ui/Button';
import SelectField from '@/components/ui/Select';
import { cn } from '@/utils/cn';
import { getCompensationReportAllPages } from '@/api/TournamentManagement/workPointApi';
import { getTournamentsAllPages } from '@/api/TournamentManagement/tournamentApi';
import { getRefereesAllPages } from '@/api/TournamentManagement/refereeApi';
import type { CompensationReportView, TournamentView, RefereeView } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';
import { formatLocalDateParam } from '@/utils/apiDate';
import { DateField } from '@/components/dateFields';
import CompensationReportTable from './components/CompensationReportTable';
import PayrollGenerationSection from './components/PayrollGenerationSection';

const Compensation: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { format: formatCurrency, code: currencyCode } = useCurrency();

  // Pre-fill date range with current month. NEVER toISOString() — east of UTC it yields yesterday
  // before 04:00 local, so an early-morning report would end a day short.
  const { defaultStart, defaultEnd } = useMemo(() => {
    const now = new Date();
    const startOfMonth = formatLocalDateParam(new Date(now.getFullYear(), now.getMonth(), 1));
    const today = formatLocalDateParam(now);
    return { defaultStart: startOfMonth, defaultEnd: today };
  }, []);

  // Filter state
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [tournamentId, setTournamentId] = useState('');
  const [refereeUserId, setRefereeUserId] = useState('');

  // Dropdown data
  const [tournaments, setTournaments] = useState<TournamentView[]>([]);
  const [referees, setReferees] = useState<RefereeView[]>([]);

  // Report state
  const [reportData, setReportData] = useState<CompensationReportView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tournament and referee lists on mount
  useEffect(() => {
    getTournamentsAllPages().then((res) => {
      if (res.errors.length === 0 && res.payload) {
        setTournaments(res.payload.items);
      }
    }).catch(() => {});
    // All pages: `pageSize: 200` was clamped to 100 server-side, so the referee filter
    // silently omitted everyone past the 100th.
    getRefereesAllPages().then((res) => {
      if (res.errors.length === 0 && res.payload) {
        setReferees(res.payload.items);
      }
    }).catch(() => {});
  }, []);

  const canGenerate = Boolean(startDate && endDate);

  // Regenerating used to `setReportData(null)` before the fetch, which tore the summary cards, the
  // table and the payroll section out of the page and replaced them with a centred spinner —
  // adjusting a date range collapsed the page height and snapped it back. The previous report now
  // stays on screen, dimmed, until the new one replaces it. Only the very first generate (nothing
  // to show yet) gets the spinner.
  const isInitialLoad = loading && !reportData;
  const isRefetching = loading && !!reportData;

  const handleGenerateReport = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);
    try {
      // All pages — the footer, "Total Officials" and the CSV must cover the same referees as the
      // server-side totals, not just the first page of 25.
      const result = await getCompensationReportAllPages({
        startDate,
        endDate,
        tournamentId: tournamentId ? Number(tournamentId) : undefined,
        refereeUserId: refereeUserId || undefined,
      });
      if (result.errors.length > 0) {
        setError(result.errors[0]?.description || t('Failed to generate report'));
      } else {
        setReportData(result.payload);
      }
    } catch {
      setError(t('Failed to generate report'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!reportData || reportData.lines.length === 0) return;

    // Three separate defects lived in the old six-line version:
    //  1. the amount column was written through formatCurrency, so every cell read "AED 1234.50"
    //     and no spreadsheet could SUM it. The amount is now a raw number and the currency moved
    //     into the header.
    //  2. names were wrapped as `"${name}"` with no escaping, so a referee name containing a double
    //     quote closed the field early and shifted every column after it. RFC 4180 doubles them.
    //  3. no BOM, so Excel decoded UTF-8 Arabic names as Windows-1252 mojibake.
    const csvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

    const headers = [
      t('Official'),
      t('Matches'),
      t('Total Work Points'),
      currencyCode
        ? t('Total Currency Amount ({{currency}})', { currency: currencyCode })
        : t('Total Currency Amount'),
    ];
    const rows = reportData.lines.map((line) => [
      csvCell(line.refereeName),
      line.matchCount,
      line.totalWorkPoints,
      line.totalCurrencyAmount,
    ]);
    const csv = [headers.map(csvCell).join(','), ...rows.map((r) => r.join(','))].join('\r\n');
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compensation-report-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tournamentOptions = useMemo(
    () => [
      { value: '', label: t('All Tournaments') },
      ...tournaments.map((tour) => ({ value: String(tour.id), label: tour.name })),
    ],
    [tournaments, t],
  );

  const refereeOptions = useMemo(
    () => [
      { value: '', label: t('All Officials') },
      ...referees.map((ref) => ({ value: ref.userId, label: ref.name })),
    ],
    [referees, t],
  );

  return (
    <PageLayout
      title={t('Compensation & Payroll')}
      description={t('View compensation reports and generate payroll entries for officials')}
      icon={DollarSign}
      accentColor="emerald"
    >
      {/* Filter Section */}
      <section
        className="bg-card border border-border rounded-lg p-6 mb-6"
        data-testid="compensation-filters"
      >
        <div className="flex items-center justify-between mb-4">
          {/* h2, not h3: PageLayout already renders the page h1 and there is no h2 between them. */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('Filters')}
          </h2>
          <Link
            to="/tournament-management/work-points"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
            data-testid="work-point-settings-link"
          >
            <Settings size={14} aria-hidden="true" />
            {t('View Work Point Settings')}
          </Link>
        </div>

        {/* Every control below used to be a bare input/select with a SIBLING <label> carrying no
            htmlFor, so all four reached screen readers unnamed. ui/Input and ui/Select generate the
            id/htmlFor pair (plus aria-invalid/aria-describedby) from the `label` prop. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          <div className="min-w-0">
            <DateField
              label={t('Start Date')}
              required
              value={startDate}
              onChange={(v) => setStartDate(v)}
              data-testid="filter-start-date"
            />
          </div>
          <div className="min-w-0">
            <DateField
              label={t('End Date')}
              required
              value={endDate}
              onChange={(v) => setEndDate(v)}
              data-testid="filter-end-date"
            />
          </div>
          <div className="min-w-0">
            {/* Radix Select, not a native <select>: the rest of the module already uses it, and it
                restores type-ahead search — this list is one row per tournament. */}
            <SelectField
              label={t('Tournament')}
              value={tournamentId}
              onChange={(value: string) => setTournamentId(value)}
              options={tournamentOptions}
              searchable
              data-testid="filter-tournament"
            />
          </div>
          <div className="min-w-0">
            {/* searchable matters most here: a company can have 100+ referees, and the native
                <select> gave no way to find one without scrolling the whole list. */}
            <SelectField
              label={t('Official')}
              value={refereeUserId}
              onChange={(value: string) => setRefereeUserId(value)}
              options={refereeOptions}
              searchable
              data-testid="filter-referee"
            />
          </div>
        </div>

        {/* Actions are their own row, not a fifth cell of the filter grid where they sat
            right-aligned against nothing and wrapped unpredictably. */}
        <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={!reportData || reportData.lines.length === 0}
            iconName="Download"
            data-testid="export-csv-btn"
          >
            {t('Export CSV')}
          </Button>
          <Button
            onClick={handleGenerateReport}
            disabled={!canGenerate}
            loading={loading}
            iconName="Search"
            data-testid="generate-report-btn"
          >
            {t('Generate Report')}
          </Button>
        </div>
      </section>

      {/* Error State — the panel had no way out of it at all: no retry, and the only other control
          was the Generate button now scrolled above. Same red panel + outline "Try Again" as
          tournament-list and referee-workload. */}
      {error && !isInitialLoad && (
        <div
          role="alert"
          className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3"
          data-testid="compensation-error"
        >
          <AlertCircle size={18} className="text-red-600 dark:text-red-400 shrink-0" aria-hidden="true" />
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateReport}
            disabled={!canGenerate || loading}
            className="ms-auto"
            data-testid="compensation-retry-btn"
          >
            {t('Try Again')}
          </Button>
        </div>
      )}

      {/* Loading State — first generate only; see isInitialLoad above. */}
      {isInitialLoad && (
        <div className="mb-6 flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 size={24} className="animate-spin" aria-hidden="true" />
            <span className="text-sm">{t('Loading data...')}</span>
          </div>
        </div>
      )}

      {/* First-Load Guidance */}
      {!reportData && !loading && !error && (
        <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="compensation-guidance">
          <FileBarChart size={48} className="mx-auto text-muted-foreground mb-4" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('compensation.generateTitle')}
          </h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            {t('compensation.generateDescription')}
          </p>
        </div>
      )}

      {/* Report — stays mounted across regenerations, dimmed rather than torn out. */}
      {reportData && (
        <div
          className={cn(
            'space-y-6 transition-opacity duration-200 ease-out motion-reduce:transition-none',
            isRefetching && 'opacity-60 pointer-events-none',
          )}
          aria-busy={isRefetching}
        >
          {/* Report Context Header */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('compensation.reportDateRange', { start: startDate, end: endDate })}
            </p>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Total Officials')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {reportData.totalCount ?? reportData.lines.length}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Total Work Points')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {reportData.totalWorkPoints.toFixed(1)}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Total Currency Amount')}</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                {formatCurrency(reportData.totalCurrencyAmount)}
              </p>
            </div>
          </div>

          {/* Table */}
          <CompensationReportTable data={reportData} startDate={startDate} endDate={endDate} />

          {/* Payroll Generation */}
          <PayrollGenerationSection
            reportData={reportData}
            startDate={startDate}
            endDate={endDate}
            tournamentId={tournamentId}
            refereeUserId={refereeUserId}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default Compensation;
